import '@material/web/icon/icon.js'
import '@material/web/fab/fab.js'
import '@operato/dataset/ox-data-ooc-brief-view.js'
import '@things-factory/worklist/dist-client/components/activity-thread-timeline.js'

import gql from 'graphql-tag'
import { css, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { ScrollbarStyles } from '@operato/styles'
import { GhostPrint } from '@operato/ghost-print'

@customElement('data-ooc-view')
export class DataOocView extends localize(i18next)(LitElement) {
  static styles = [
    ScrollbarStyles,
    css`
      :host {
        display: block;
        position: relative;
        background-color: var(--md-sys-color-surface);

        overflow: auto;
      }

      ox-data-ooc-brief-view {
        padding: var(--spacing-large);
      }

      activity-thread-timeline {
        margin: var(--spacing-medium);
      }

      md-fab {
        position: fixed;
        bottom: 15px;
        right: 16px;
        text-decoration: auto;
        opacity: 0.5;
      }

      @media print {
        md-fab {
          display: none;
        }
      }
    `
  ]

  @property({
    type: String,
    attribute: 'data-ooc-id'
  })
  dataOocId?: string

  @state() dataOoc?: any

  private printHandler = this.handlePrint.bind(this)

  render() {
    if (!this.dataOoc) {
      return nothing
    }

    const reviewActivityThread = this.dataOoc?.reviewActivityInstance?.activityThreads?.[0]
    const resolveActivityThread = this.dataOoc?.resolveActivityInstance?.activityThreads?.[0]

    return html`
      <ox-data-ooc-brief-view .dataOoc=${this.dataOoc}></ox-data-ooc-brief-view>
      ${reviewActivityThread
        ? html`
            <activity-thread-timeline
              subtitle=${String(i18next.t('label.ooc review timeline'))}
              .activityThread=${reviewActivityThread}
              short-form
            ></activity-thread-timeline>
          `
        : nothing}
      ${resolveActivityThread
        ? html`
            <activity-thread-timeline
              subtitle=${String(i18next.t('label.ooc resolve timeline'))}
              .activityThread=${resolveActivityThread}
              short-form
            ></activity-thread-timeline>
          `
        : nothing}

      <md-fab
        title="print"
        @click=${() =>
          GhostPrint.print({
            src: `./data-ooc/${this.dataOoc.id}`
          })}
      >
        <md-icon slot="icon">print</md-icon>
      </md-fab>
    `
  }

  updated(changes) {
    if (changes.has('dataOocId')) {
      this.fetchDataOoc()
    }
  }

  async fetchDataOoc() {
    const id = this.dataOocId

    const response = await client.query({
      query: gql`
        query ($id: String!) {
          dataOoc(id: $id) {
            id
            name
            description
            useCase
            dataSet {
              id
              name
              monitorType
              monitorView
            }
            key01
            key02
            key03
            key04
            key05
            partitionKeys
            dataItems {
              name
              description
              active
              hidden
              tag
              group
              subgroup
              type
              unit
              options
              quota
              spec
              stat
            }
            data
            rawData
            judgment
            ooc
            oos
            state
            history
            workDate
            workShift
            correctiveInstruction
            correctiveAction
            reviewedAt
            reviewer {
              id
              name
            }
            correctedAt
            corrector {
              id
              name
            }
            updater {
              id
              name
            }
            updatedAt
            collectedAt
            reviewActivityInstance {
              ...ActivityInstanceFragment
            }
            resolveActivityInstance {
              ...ActivityInstanceFragment
            }
          }
        }

        fragment ActivityInstanceFragment on ActivityInstance {
          activityThreads {
            assignee {
              name
            }
            output
            activityApprovals {
              judgment
              approver {
                name
              }
              comment
              createdAt
              updatedAt
              terminatedAt
            }
            activityThreadHistories {
              transaction
              reason
              updater {
                name
              }
              updatedAt
              state
              output
            }
          }
        }
      `,
      variables: {
        id
      }
    })

    this.dataOoc = response.data.dataOoc
  }

  handlePrint(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
      event.preventDefault()

      GhostPrint.print({
        src: `./data-ooc/${this.dataOoc.id}`
      })
    }
  }

  connectedCallback(): void {
    super.connectedCallback()
    window.addEventListener('keydown', this.printHandler)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    window.removeEventListener('keydown', this.printHandler)
  }
}
